import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, ShoppingCart, Heart, Share2, Check } from 'lucide-react';
import { getProductById } from '../services/dataService';
import { formatPrice } from '../utils/helpers';
import { useCartStore } from '../store/cartStore';
import Button from '../components/ui/Button';
import { useAddToCartAnimation } from '../hooks/useAddToCartAnimation';

const ProductDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const product = getProductById(id || '');
  const addItem = useCartStore((state) => state.addItem);
  const { isAnimating, triggerAnimation } = useAddToCartAnimation();
  
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);

  if (!product) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-secondary-900 mb-4">
            Product not found
          </h1>
          <Button onClick={() => navigate('/gallery')}>
            Back to Gallery
          </Button>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    addItem(product, quantity);
    triggerAnimation();
  };

  const nextImage = () => {
    setSelectedImage((prev) => 
      prev === product.images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setSelectedImage((prev) => 
      prev === 0 ? product.images.length - 1 : prev - 1
    );
  };

  return (
    <div className="min-h-screen bg-secondary-50">
      <div className="container-max section-padding">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-4"
          >
            {/* Main Image */}
            <div className="relative aspect-square bg-white rounded-xl overflow-hidden shadow-lg">
              <img
                src={product.images[selectedImage]}
                alt={product.title}
                className="w-full h-full object-cover"
              />
              
              {/* Navigation Arrows */}
              {product.images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>

            {/* Thumbnail Images */}
            {product.images.length > 1 && (
              <div className="flex space-x-2 overflow-x-auto">
                {product.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                      selectedImage === index
                        ? 'border-primary-600'
                        : 'border-secondary-200 hover:border-secondary-300'
                    }`}
                  >
                    <img
                      src={image}
                      alt={`${product.title} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* Product Information */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            {/* Header */}
            <div>
              <h1 className="text-3xl md:text-4xl font-serif font-bold text-secondary-900 mb-2">
                {product.title}
              </h1>
              <p className="text-2xl font-bold text-primary-600 mb-4">
                {formatPrice(product.price)}
              </p>
              <div className="flex items-center space-x-4">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  product.inStock
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {product.inStock ? 'In Stock' : 'Out of Stock'}
                </span>
                {product.featured && (
                  <span className="px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm font-medium">
                    Featured
                  </span>
                )}
              </div>
            </div>

            {/* Description */}
            <div>
              <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                Description
              </h3>
              <p className="text-secondary-600 leading-relaxed">
                {product.description}
              </p>
            </div>

            {/* Dimensions */}
            {product.dimensions && (
              <div>
                <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                  Dimensions
                </h3>
                <p className="text-secondary-600">
                  {product.dimensions}
                </p>
              </div>
            )}

            {/* Customization Options */}
            {product.customizability && product.customizability.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                  Customization Options
                </h3>
                <ul className="space-y-1">
                  {product.customizability.map((option, index) => (
                    <li key={index} className="flex items-center text-secondary-600">
                      <span className="w-2 h-2 bg-primary-600 rounded-full mr-3"></span>
                      {option}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Add to Cart Section */}
            <div className="border-t border-secondary-200 pt-6 space-y-4">
              {/* Quantity */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Quantity
                </label>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 border border-secondary-300 rounded-lg flex items-center justify-center hover:bg-secondary-50 transition-colors"
                  >
                    -
                  </button>
                  <span className="w-16 text-center font-medium">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-10 h-10 border border-secondary-300 rounded-lg flex items-center justify-center hover:bg-secondary-50 transition-colors"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleAddToCart}
                  disabled={!product.inStock}
                  className="flex-1"
                >
                  {isAnimating ? (
                    <Check className="w-5 h-5 mr-2 text-green-600" />
                  ) : (
                    <ShoppingCart className="w-5 h-5 mr-2" />
                  )}
                  {isAnimating ? 'Added!' : 'Add to Cart'}
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                >
                  <Heart className="w-5 h-5 mr-2" />
                  Wishlist
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                >
                  <Share2 className="w-5 h-5 mr-2" />
                  Share
                </Button>
              </div>
            </div>

            {/* Additional Info */}
            <div className="border-t border-secondary-200 pt-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-secondary-500">Category:</span>
                  <p className="font-medium text-secondary-900">{product.category}</p>
                </div>
                <div>
                  <span className="text-secondary-500">SKU:</span>
                  <p className="font-medium text-secondary-900">{product.id}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails; 